import React, { useMemo } from "react";
import { GistType } from "../../services/types";
import { TreeDepthOption } from "./DiscussionMap";

interface DiscussionMapTreeProps {
  gists: GistType[];
  activeId: number | null;
  onSelect: (gist: GistType) => void;
  onOpenFullGist?: (gist: GistType) => void;
  depth?: number;
  maxDepth: TreeDepthOption;
  focusSelectedBranch?: boolean;
  collapseSiblings?: boolean;
  showRecentOnly?: boolean;
  activityMap?: Map<number, { lastActivityAt: number | null, isRecentSelfOrSubtree: boolean }>;
}

const dateFormatter = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });

const countAllReplies = (gistList: GistType[]): number => {
  let count = 0;
  for (const g of gistList) {
    count++;
    if (Array.isArray(g.gists)) count += countAllReplies(g.gists);
  }
  return count;
};

const getPathToNode = (gists: GistType[], targetId: number | null): Set<number> => {
  if (!targetId) return new Set();
  const path = new Set<number>();
  
  const dfs = (nodes: GistType[], currentPath: number[]): boolean => {
    for (const node of nodes) {
      currentPath.push(node.id);
      if (node.id === targetId) {
        currentPath.forEach(id => path.add(id));
        return true;
      }
      if (Array.isArray(node.gists) && dfs(node.gists, currentPath)) {
        return true;
      }
      currentPath.pop();
    }
    return false;
  };
  
  dfs(gists, []);
  return path;
};

const DiscussionMapTreeNode: React.FC<{
  gist: GistType;
  activeId: number | null;
  onSelect: (gist: GistType) => void;
  onOpenFullGist?: (gist: GistType) => void;
  depth: number;
  maxDepth: TreeDepthOption;
  focusSelectedBranch?: boolean;
  collapseSiblings?: boolean;
  showRecentOnly?: boolean;
  activityMap?: Map<number, { lastActivityAt: number | null, isRecentSelfOrSubtree: boolean }>;
  activePath?: Set<number>;
  inFocusedBranch?: boolean;
}> = ({ 
  gist, 
  activeId, 
  onSelect,
  onOpenFullGist,
  depth, 
  maxDepth, 
  focusSelectedBranch, 
  collapseSiblings,
  showRecentOnly,
  activityMap,
  activePath,
  inFocusedBranch = false
}) => {
  const isFocusModeActive = !!(focusSelectedBranch && activePath && activePath.size > 0);
  const isCollapseSiblingsActive = !!(collapseSiblings && activePath && activePath.size > 0);
  
  const inPathOrDescendantMode = isFocusModeActive || isCollapseSiblingsActive;
  const isPathNode = inPathOrDescendantMode && activePath!.has(gist.id);
  const isDescendantOfFocus = inPathOrDescendantMode && (inFocusedBranch || gist.id === activeId);

  const meta = activityMap?.get(gist.id);
  const isRecentSubtree = meta?.isRecentSelfOrSubtree ?? false;

  // Focus mode hides unrelated nodes completely
  let shouldRender = true;
  if (isFocusModeActive) {
    shouldRender = isPathNode || inFocusedBranch || isDescendantOfFocus;
  } else if (showRecentOnly) {
    shouldRender = isRecentSubtree;
  }

  if (!shouldRender) return null;

  const isActive = gist.id === activeId;
  const point = gist.versions?.[0]?.point;
  const commentCount =
    gist.versions?.[0]?.edits?.reduce(
      (acc, edit) => acc + (edit.comments?.length ?? 0),
      0
    ) ?? 0;
  const versionCount = gist.versions?.length ?? 0;
  const createdAt = gist.createdAt
    ? dateFormatter.format(Date.parse(gist.createdAt))
    : null;

  const hasChildren = Array.isArray(gist.gists) && gist.gists.length > 0;
  const maxDepthNum = maxDepth === "all" ? Infinity : parseInt(maxDepth, 10);
  
  const isSiblingBranch = isCollapseSiblingsActive && !isPathNode && !inFocusedBranch && !isDescendantOfFocus;

  // Determine if children should unfold
  let showChildren = false;
  if (isFocusModeActive) {
    showChildren = true;
  } else if (isSiblingBranch) {
    showChildren = false;
  } else {
    showChildren = depth < maxDepthNum;
  }

  return (
    <div
      className={`discussion-map-tree-branch${depth > 1 ? " discussion-map-tree-branch-child" : ""}`}
    >
      <button
        type="button"
        className={`discussion-map-tree-node${isActive ? " is-active" : ""}`}
        style={{ "--dm-depth": depth } as React.CSSProperties}
        onClick={() => onSelect(gist)}
        onDoubleClick={() => onOpenFullGist?.(gist)}
        aria-current={isActive ? "true" : undefined}
        title="Single-click to preview. Double-click to open this GIST in Reader View."
      >
        <span className="discussion-map-tree-node-title">{gist.title}</span>
        {point && (
          <span className="discussion-map-tree-node-point">{point}</span>
        )}
        <span className="discussion-map-tree-node-meta">
          {meta && meta.lastActivityAt && isRecentSubtree && (
            <span
              className="discussion-map-tree-node-badge discussion-map-tree-node-badge-recent"
              title="Recent activity within the last 7 days"
            >
              Recent
            </span>
          )}
          {versionCount > 0 && (
            <span
              className="discussion-map-tree-node-badge discussion-map-tree-node-badge-version"
              title={`${versionCount} version${versionCount !== 1 ? "s" : ""}`}
            >
              {versionCount}v
            </span>
          )}
          {commentCount > 0 && (
            <span
              className="discussion-map-tree-node-badge discussion-map-tree-node-badge-comment"
              title={`${commentCount} comment${commentCount !== 1 ? "s" : ""}`}
            >
              💬 {commentCount}
            </span>
          )}
          {createdAt && (
            <span className="discussion-map-tree-node-date">{createdAt}</span>
          )}
        </span>
      </button>

      {hasChildren && showChildren && (
        <div className="discussion-map-tree-children">
          {gist.gists.map((child) => (
            <DiscussionMapTreeNode
              key={child.id}
              gist={child}
              activeId={activeId}
              onSelect={onSelect}
              onOpenFullGist={onOpenFullGist}
              depth={depth + 1}
              maxDepth={maxDepth}
              focusSelectedBranch={focusSelectedBranch}
              collapseSiblings={collapseSiblings}
              showRecentOnly={showRecentOnly}
              activityMap={activityMap}
              activePath={activePath}
              inFocusedBranch={isDescendantOfFocus}
            />
          ))}
        </div>
      )}
      {hasChildren && !showChildren && (
        <div className="discussion-map-tree-hidden-replies">
          {isSiblingBranch 
            ? `Collapsed branch (+${countAllReplies(gist.gists)})`
            : `+${countAllReplies(gist.gists)} hidden replies`
          }
        </div>
      )}
    </div>
  );
};

const DiscussionMapTree: React.FC<DiscussionMapTreeProps> = ({
  gists,
  activeId,
  onSelect,
  onOpenFullGist,
  depth = 1,
  maxDepth,
  focusSelectedBranch,
  collapseSiblings,
  showRecentOnly,
  activityMap,
}) => {
  const activePath = useMemo(() => {
    if ((focusSelectedBranch || collapseSiblings) && activeId) {
      return getPathToNode(gists, activeId);
    }
    return new Set<number>();
  }, [gists, activeId, focusSelectedBranch, collapseSiblings]);

  if (!gists || gists.length === 0) {
    return (
      <div className="discussion-map-tree-empty">No GISTs to display.</div>
    );
  }

  const showFocusHint = focusSelectedBranch && !activeId;
  const showCollapseHint = collapseSiblings && !activeId;
  const hasNoRecent = showRecentOnly && gists.every(g => !activityMap?.get(g.id)?.isRecentSelfOrSubtree);

  return (
    <div className="discussion-map-tree-root">
      {hasNoRecent && (
        <div className="discussion-map-tree-empty-hint">
          No recent activity in the last 7 days.
        </div>
      )}
      {(showFocusHint || showCollapseHint) && (
        <div className="discussion-map-tree-focus-hint">
          {showFocusHint 
            ? "Select a GIST to focus a branch." 
            : "Select a GIST to collapse sibling branches."}
        </div>
      )}
      {gists.map((gist) => (
        <DiscussionMapTreeNode
          key={gist.id}
          gist={gist}
          activeId={activeId}
          onSelect={onSelect}
          onOpenFullGist={onOpenFullGist}
          depth={depth}
          maxDepth={maxDepth}
          focusSelectedBranch={focusSelectedBranch}
          collapseSiblings={collapseSiblings}
          showRecentOnly={showRecentOnly}
          activityMap={activityMap}
          activePath={activePath}
        />
      ))}
    </div>
  );
};

export default DiscussionMapTree;
